import React from 'react';
import { MenuItem } from '@mui/material';

export const TEAM_TAGGING_OPTIONS = ['BM', 'CANDIDATE', 'EMPLOYER'];

const TEAM_OPTION_LABELS = {
    BM: 'BM',
    CANDIDATE: 'Candidate',
    EMPLOYER: 'Employer',
};

/**
 * Maps API / human-readable team strings to a MenuItem `value` (BM | CANDIDATE | EMPLOYER).
 * Returns '' only when there is no reasonable match (empty or unknown).
 */
export function normalizeTeamToOption(raw) {
    const v = String(raw ?? '').trim();
    if (!v) {
        return '';
    }
    if (TEAM_TAGGING_OPTIONS.includes(v)) {
        return v;
    }
    const lower = v.toLowerCase();
    if (lower === 'bm') {
        return 'BM';
    }
    if (lower === 'candidate') {
        return 'CANDIDATE';
    }
    if (lower === 'employer') {
        return 'EMPLOYER';
    }
    return '';
}

/** Create / controlled-field helper: coerce to a known option or blank. */
export function normalizedTeamEditValue(raw) {
    return normalizeTeamToOption(raw);
}

/**
 * When loading rows from the API: use canonical enum when we recognize the string,
 * otherwise keep the original value for display and round-trip.
 */
export function normalizeTeamFieldForRow(raw) {
    const canon = normalizeTeamToOption(raw);
    return canon !== '' ? canon : String(raw ?? '').trim();
}

/** Renders the closed Select label so the chosen team is always readable. */
export function teamSelectRenderValue(selected) {
    if (selected === '' || selected == null) {
        return <em style={{ color: 'rgba(0, 0, 0, 0.55)' }}>Select team</em>;
    }
    const canon = normalizeTeamToOption(selected);
    if (canon) {
        return TEAM_OPTION_LABELS[canon] ?? canon;
    }
    return String(selected);
}

/** Spread onto team `<TextField select>` (edit modal + create dialog). */
export function getTeamSelectTextFieldProps() {
    return {
        variant: 'outlined',
        InputLabelProps: { shrink: true },
        SelectProps: {
            displayEmpty: true,
            renderValue: teamSelectRenderValue,
            MenuProps: {
                PaperProps: {
                    sx: {
                        '& .MuiMenuItem-root': {
                            color: 'rgba(0, 0, 0, 0.87)',
                        },
                    },
                },
            },
        },
        sx: {
            '& .MuiSelect-select': { color: 'rgba(0, 0, 0, 0.87)' },
            '& .MuiOutlinedInput-input': { color: 'rgba(0, 0, 0, 0.87)' },
        },
    };
}

/**
 * Edit/create only: placeholder + BM / Candidate / Employer (no extra row values).
 * Table cells still show the stored `team` string from the API.
 */
export function teamSelectMenuItems() {
    return [
        <MenuItem key="_empty" value="">
            <em>Select team</em>
        </MenuItem>,
        ...TEAM_TAGGING_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
                {TEAM_OPTION_LABELS[opt] ?? opt}
            </MenuItem>
        )),
    ];
}

/** Label for read-only table cells (BM / Candidate / Employer). */
export function teamCellDisplayLabel(raw) {
    const canon = normalizeTeamToOption(raw);
    if (canon) {
        return TEAM_OPTION_LABELS[canon] ?? canon;
    }
    const s = String(raw ?? '').trim();
    return s || '—';
}
